import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAbility } from '@casl/vue';
import {
  usePostAccountShipmentsBulkArchive,
  usePostAccountShipmentsBulkDelete,
} from '@gpx-spa/queries';
import {
  COMMON_ARCHIVE_ERROR,
  COMMON_ARCHIVE_SUCCESS,
  COMMON_DELETE_ERROR,
  COMMON_DELETE_SUCCESS,
} from '@/config/constants';
import { getNoun, getPronoun } from '@/utils/noun';
import type { ShipmentListRow } from '@/types/shipments';
import { isShipmentArchived } from '@/types/shipments';
import { canEditShipmentForUser } from '@/utils/shipment-draft-access';
import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';

function upperFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function useShipmentListActions(options: {
  onSelectionCleared?: () => void;
}) {
  const store = useStore();
  const router = useRouter();
  const { can } = useAbility();

  const accountId = computed(() => store.getters['auth/accountId'] as number);

  const canManage = computed(() => can('manage', 'Shipments'));

  const bulkArchive = usePostAccountShipmentsBulkArchive();
  const bulkDelete = usePostAccountShipmentsBulkDelete();

  function clearSelection() {
    options.onSelectionCleared?.();
  }

  function editShipment(shipment: ShipmentListRow | V2ShipmentDetail) {
    router.push({
      name: 'ShipmentEditView',
      params: { id: String(shipment.id) },
    });
  }

  async function runClose(ids: number[]) {
    await bulkArchive.mutateAsync({
      accountId: accountId.value,
      ids,
    });
    clearSelection();
  }

  async function runDelete(ids: number[]) {
    await bulkDelete.mutateAsync({
      accountId: accountId.value,
      ids,
    });
    clearSelection();
  }

  function confirmClose(ids: number[]) {
    if (!ids.length || !canManage.value) return;

    const pronoun = getPronoun(ids);
    const noun = getNoun(ids, 'shipment');
    store.dispatch('modal/open', {
      name: 'CommonConfirmationModal',
      params: {
        actionLabel: 'Close',
        title: upperFirst(noun),
        body: `Are you sure you want to close ${pronoun} ${noun}?`,
        onOk: () => runClose(ids),
        successMessage: COMMON_ARCHIVE_SUCCESS,
        errorMessage: COMMON_ARCHIVE_ERROR,
      },
    });
  }

  function confirmDelete(ids: number[]) {
    if (!ids.length || !canManage.value) return;

    const pronoun = getPronoun(ids);
    const noun = getNoun(ids, 'shipment');
    store.dispatch('modal/open', {
      name: 'CommonConfirmationModal',
      params: {
        actionLabel: 'Delete',
        title: upperFirst(noun),
        body: `Are you sure you want to delete ${pronoun} ${noun}?`,
        onOk: () => runDelete(ids),
        successMessage: COMMON_DELETE_SUCCESS,
        errorMessage: COMMON_DELETE_ERROR,
      },
    });
  }

  function canCloseShipment(shipment: ShipmentListRow): boolean {
    return canManage.value && !isShipmentArchived(shipment);
  }

  function canEditShipment(shipment: ShipmentListRow): boolean {
    return canEditShipmentForUser(shipment, canManage.value);
  }

  function canDeleteShipment(): boolean {
    return canManage.value;
  }

  return {
    canManage,
    editShipment,
    confirmClose,
    confirmDelete,
    canCloseShipment,
    canEditShipment,
    canDeleteShipment,
  };
}
